import React from 'react';

import _ from 'lodash';
import { ArrowDropDown } from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';

import { Analytics } from '@/app/analytics/Analytics';
import { AnalyticsEvent } from '@/app/analytics/AnalyticsEvent';
import { Log } from '@/parsing/Log';

type Props = {
  log: Log;
};

type State = {
  open: boolean;
};

export class OpenLogAsText extends React.Component<Props, State> {
  private anchorRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.state = {
      open: false,
    };
  }

  render() {
    return (
      <>
        <ButtonGroup variant="contained" ref={this.anchorRef} aria-label="split button">
          <Button style={{ whiteSpace: 'nowrap' }} onClick={() => this.handleClick()}>
            Open as text
          </Button>
          <Button
            size="small"
            aria-controls={this.state.open ? 'split-button-menu' : undefined}
            aria-expanded={this.state.open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={() => this.handleToggle()}
          >
            <ArrowDropDown />
          </Button>
        </ButtonGroup>
        <Popper
          open={this.state.open}
          anchorEl={this.anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={() => this.handleClose()}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    <MenuItem onClick={() => this.handleMenuItemClick()}>Download as text</MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }

  private handleClick() {
    this.openLogAsRawText();
  }

  private handleToggle() {
    this.setState((prevState) => ({
      open: !prevState.open,
    }));
  }

  private handleClose() {
    this.setState({ open: false });
  }

  private handleMenuItemClick() {
    this.downloadLogAsRawText();
    this.setState({ open: false });
  }

  private openLogAsRawText() {
    Analytics.log(AnalyticsEvent.OpenAsTextClicked);

    const log = this.props.log;

    let content = `<table style="font-family: monospace; overflow-wrap: anywhere">`;
    log.entries.forEach((e) =>
      e.originalLines.forEach((l) => {
        content += `<tr>
                <td style="vertical-align: top; width: 64px">
                    ${l.lineIndex + 1}
                </td>
                <td>${_.escape(l.content)}</td>
                </tr>`;
      }),
    );
    content += `</table>`;

    const blob = new Blob([content], {
      type: 'text/html;charset=utf8',
    });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  private downloadLogAsRawText() {
    Analytics.log(AnalyticsEvent.DownloadAsTextClicked);

    const log = this.props.log;
    OpenLogAsText.downloadLogAsRawText(log);
  }

  static downloadLogAsRawText(log: Log) {
    let content = ``;
    log.entries.forEach((e) =>
      e.originalLines.forEach((l) => {
        content += `${l.content}\n`;
      }),
    );

    const fileNames = log.parserMetadata.originalFileNames;
    const fileName = this.composeLogFileName(fileNames.length > 0 ? fileNames[0] : undefined);
    this.downloadRawText(content, fileName);
  }

  static composeLogFileName(originalFileName: string | undefined): string {
    let fileName = 'log.txt';

    if (originalFileName && originalFileName.endsWith('.txt.gz')) {
      const suffixToRemove = '.gz';
      fileName = originalFileName.substring(0, originalFileName.length - suffixToRemove.length);
    }

    return fileName;
  }

  static downloadRawText(content: string, fileName: string) {
    const blob = new Blob([content], {
      type: 'text/plain;charset=utf8',
    });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.setAttribute('download', fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
